import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Leaf } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthPage() {
  const navigate = useNavigate();
  const { setUser, fetchCartCount } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '' });

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/`;
    const appName = encodeURIComponent('JFRAI Family Brews');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&app_name=${appName}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData, { withCredentials: true });
      setUser(response.data.user);
      await fetchCartCount();
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerData, { withCredentials: true });
      setUser(response.data.user);
      await fetchCartCount();
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-cream-50 to-sage-100 botanical-pattern flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_34476667-a3f6-4f7e-89c3-0dc66402e9b8/artifacts/9u4cd63c_JFRAI%20Family%20Rooted%20logo.png" 
            alt="JFRAI Family Rooted Logo" 
            className="w-32 h-32 object-contain mx-auto mb-4"
          />
          <h1 className="text-4xl font-bold text-sage-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Welcome to JFRAI Family Flavorful Brews & Tings
          </h1>
        </div>

        <Card className="bg-[#81D8D0]">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-white">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin}>
                <CardHeader>
                  <CardTitle className="text-sage-900 font-bold">Login to your account</CardTitle>
                  <CardDescription className="text-sage-800 font-semibold">Enter your credentials to access your account</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="login-email" className="text-sage-900 font-semibold text-base">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      className="bg-white"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password" className="text-sage-900 font-semibold text-base">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      className="bg-white"
                      data-testid="login-password-input"
                    />
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col space-y-2">
                  <Button type="submit" className="w-full bg-sage-600 hover:bg-sage-700" disabled={loading} data-testid="login-submit-button">
                    {loading ? 'Logging in...' : 'Login'}
                  </Button>
                  <div className="relative w-full">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-sage-700" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-[#81D8D0] px-2 text-sage-900 font-semibold">Or continue with</span>
                    </div>
                  </div>
                  <Button type="button" variant="outline" className="w-full bg-white border-sage-700 text-sage-900 hover:bg-sage-50" onClick={handleGoogleLogin} data-testid="google-login-button">
                    Google
                  </Button>
                </CardFooter>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister}>
                <CardHeader>
                  <CardTitle className="text-sage-900 font-bold">Create an account</CardTitle>
                  <CardDescription className="text-sage-800 font-semibold">Enter your details to get started</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="register-name" className="text-sage-900 font-semibold text-base">Name</Label>
                    <Input
                      id="register-name"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      required
                      className="bg-white"
                      data-testid="register-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-email" className="text-sage-900 font-semibold text-base">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      className="bg-white"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-password" className="text-sage-900 font-semibold text-base">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      className="bg-white"
                      data-testid="register-password-input"
                    />
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col space-y-2">
                  <Button type="submit" className="w-full bg-sage-600 hover:bg-sage-700" disabled={loading} data-testid="register-submit-button">
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                  <div className="relative w-full">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-sage-700" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-[#81D8D0] px-2 text-sage-900 font-semibold">Or continue with</span>
                    </div>
                  </div>
                  <Button type="button" variant="outline" className="w-full bg-white border-sage-700 text-sage-900 hover:bg-sage-50" onClick={handleGoogleLogin} data-testid="google-register-button">
                    Google
                  </Button>
                </CardFooter>
              </form>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
      
      {/* Footer with Copyright */}
      <footer className="mt-8 text-center">
        <p className="text-sage-700 text-sm">
          © 2025 JFRAI Family Flavorful Brews & Tings. All rights reserved.
        </p>
        <p className="text-sage-600 text-xs mt-1">
          Natural healing through ancestral wisdom.
        </p>
      </footer>
    </div>
  );
}
